import { WIDGET_INTERACTION } from 'constants/mixpanel';
import { WidgetId } from 'types/idTypes';
import { InfographState } from 'types/infographTypes';
import {
  Interaction,
  InteractionType,
  InteractionsMap,
  InteractiveElement,
  TooltipInteraction,
} from 'types/interactiveWidget.types';
import { WidgetType } from 'types/widget.types';
import { checkForCheckbox } from 'widgets/TextBasedWidgets/TableWidget/TableWidget.helpers';
import { TableWidgetData } from 'widgets/TextBasedWidgets/TableWidget/TableWidget.types';
import { INTERACTIVE_WIDGET_TYPES } from 'widgets/Widget.config';
import { getPageWidgetIds, getWidgetTypeFromId } from 'widgets/Widget.helpers';

import { SUPPORTED_WIDGET_INTERACTIONS } from './InteractiveMenu.config';

/**
 * Check if the widget has any active interaction.
 *
 * @param interactions
 * @returns boolean
 */
export const hasInteraction = (interactions: InteractionsMap | undefined) => {
  if (!interactions)
    return {
      type: null,
      isActive: false,
    };

  if (!!interactions[InteractionType.Expandable]) {
    return {
      type: InteractionType.Expandable,
      isActive: true,
    };
  }

  if (!!interactions[InteractionType.Tooltip]) {
    const tooltipInteraction = interactions[InteractionType.Tooltip] as TooltipInteraction;
    return {
      type: InteractionType.Tooltip,
      isActive: !!tooltipInteraction.data?.content,
    };
  }

  return {
    type: null,
    isActive: false,
  };
};

/**
 * Get supported interactions for a widget type.
 *
 * @param widgetType
 * @returns array of supported interactions
 */
export const getSupportedInteractions = (widgetType: WidgetType): InteractionType[] => {
  return Object.entries(SUPPORTED_WIDGET_INTERACTIONS).reduce((acc, [interactionType, supportedWidgetTypes]) => {
    if (supportedWidgetTypes.includes(widgetType)) {
      acc.push(interactionType as InteractionType);
    }
    return acc;
  }, [] as InteractionType[]);
};

/**
 * Check if the widget type supports at least one interaction.
 *
 * @param widgetId
 * @returns boolean
 */
export const checkIsWidgetTypeSupported = (widgetId: WidgetId) => {
  const widgetType = getWidgetTypeFromId(widgetId);
  return Object.values(SUPPORTED_WIDGET_INTERACTIONS).some((supportedWidgetTypes) =>
    supportedWidgetTypes.includes(widgetType),
  );
};

/**
 * Get the Mixpanel event name and props for an interactive menu event.
 *
 * @param interactionType
 * @param data
 * @param oldData
 * @returns
 */
export const getInteractiveMenuMPEvent = (
  interactionType: InteractionType,
  data: Interaction | null,
  oldData: Interaction | null,
) => {
  const deleteEventName = WIDGET_INTERACTION.Deleted.eventName;
  const createEventName = WIDGET_INTERACTION.Created.eventName;
  const editEventName = WIDGET_INTERACTION.Edited.eventName;

  switch (interactionType) {
    case InteractionType.Expandable: {
      const isDelete = !data;
      return {
        eventName: isDelete ? deleteEventName : createEventName,
        eventProps: { interaction_type: WIDGET_INTERACTION.interactionType.Expandable },
      };
    }
    case InteractionType.Tooltip: {
      const isDelete = !data || !(data as TooltipInteraction).data?.content;
      const isEdit = !!oldData && !!data && (oldData as TooltipInteraction).data?.content;
      return {
        eventName: isDelete ? deleteEventName : isEdit ? editEventName : createEventName,
        eventProps: { interaction_type: WIDGET_INTERACTION.interactionType.Label },
      };
    }
    default:
      return null;
  }
};

function getEnumKeyByValue(enumObj: Record<string, string>, value: string): string | undefined {
  return Object.keys(enumObj).find((key) => enumObj[key] === value);
}

const WIDGET_NAME_MAP: Partial<Record<keyof typeof WidgetType, string>> = {
  Lottie: 'GIF',
  Table: 'Checklist',
};

// Function to get custom name or original key for widget type
function getCustomOrOriginalName(key: keyof typeof WidgetType): string {
  return WIDGET_NAME_MAP[key] || key;
}

/**
 *
 * Get every interactive widget type in the design.
 * Returns an array of unique interactive widget names.
 *
 * @param infograph
 * @returns  string[]
 */
export const getInteractiveWidgets = (infograph: InfographState): string[] => {
  const { widgets, pages } = infograph;
  const interactiveWidgetSet = new Set<string>();

  Object.keys(pages).forEach((pageId) => {
    const page = pages[pageId];
    const pageWidgetIds = getPageWidgetIds(page, widgets);

    pageWidgetIds.forEach((widgetId) => {
      const widgetType = getWidgetTypeFromId(widgetId);

      if (INTERACTIVE_WIDGET_TYPES.includes(widgetType as WidgetType)) {
        const widgetData = widgets[widgetId] as TableWidgetData;
        if (widgetType === WidgetType.Table) {
          // Not all tables are interactive, so check if it's an interactive list
          const isInteractiveList = checkForCheckbox(widgetData.proseMirrorData);
          if (isInteractiveList) {
            interactiveWidgetSet.add('Checklist');
          }

          return;
        }

        if (widgetData && widgetData.hasOwnProperty('interactions')) {
          const interactionType = hasInteraction((widgetData as InteractiveElement).interactions).type;
          if (interactionType) {
            interactiveWidgetSet.add(interactionType);
          }
        }

        const enumKey = getEnumKeyByValue(WidgetType, widgetType);
        if (enumKey) {
          const customName = getCustomOrOriginalName(enumKey as keyof typeof WidgetType);
          interactiveWidgetSet.add(customName);
        }
      } else {
        // it's basic element, but it may contain interactions (tooltip / expandable), so we should check it
        const widgetData = widgets[widgetId];
        if (widgetData && widgetData.hasOwnProperty('interactions')) {
          const interactionType = hasInteraction((widgetData as InteractiveElement).interactions).type;
          if (interactionType) {
            interactiveWidgetSet.add(interactionType);
          }
        }
      }
    });
  });

  return Array.from(interactiveWidgetSet);
};
